import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileIcon } from '@/shared/assets/icons/file-icon';
import { ClearButton } from '@/shared/components/ui/button/clear-button/clear-button';
import {
  TFileInfo,
  TFileUploadedProps,
} from '@/shared/components/ui/form/components/file-dropzone/components/file-uploaded/file-uploaded.types';
import {
  FileInfo,
  FileInfoSecondary,
  FileUploadedContainer,
} from '@/shared/components/ui/form/components/file-dropzone/file-dropzone.styled';
import { FormSelectedItemContainer } from '@/shared/components/ui/form-selected-item-container/form-selected-item-container';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { getFileKey } from '@/shared/utils/document';
import { formatFileSize } from '@/shared/utils/format';

export const FileUploaded: React.FC<TFileUploadedProps> = ({
  value,
  disabled,
  onRemove,
  renderFileOptions,
}) => {
  const { t } = useTranslation();
  const isMultiple = Array.isArray(value);

  const createFileInfo = (file: File): TFileInfo & { originalFile: File } => ({
    name: file.name,
    size: formatFileSize(file.size),
    originalFile: file,
  });

  const getFileInfo = () => {
    if (!value) return [];
    return Array.isArray(value)
      ? value.map(createFileInfo)
      : [createFileInfo(value)];
  };

  const handleClick = (file: File) => {
    if (onRemove) {
      onRemove(file);
    }
  };

  const renderFileLabel = (info: TFileInfo) => (
    <FileInfo>
      <FileInfoSecondary>
        {t('ui:selected_file', { count: 1 })}:
      </FileInfoSecondary>
      <TruncatedText
        text={info.name}
        truncateFromBack
        maxLength={32}
        variant="p8"
      />
      <FileInfoSecondary>({info.size})</FileInfoSecondary>
    </FileInfo>
  );

  const renderDeleteButton = (file: File) => (
    <ClearButton
      tooltip={t('common:remove')}
      tooltipProps={{ placement: 'left' }}
      disabled={disabled}
      onClick={() => handleClick(file)}
      circle
    />
  );

  if (renderFileOptions) {
    return (
      <Stack gap="0.5rem" width="100%" marginTop="0.5rem">
        {getFileInfo().map((info, index) => (
          <FormSelectedItemContainer
            key={getFileKey(info.originalFile, index)}
            disabled={disabled}
            icon={<FileIcon variant="primary" />}
            label={renderFileLabel(info)}
            action={renderDeleteButton(info.originalFile)}
            options={renderFileOptions(info.originalFile, index)}
          />
        ))}
      </Stack>
    );
  }

  return (
    <FileUploadedContainer $multiple={isMultiple}>
      {getFileInfo().map((info, index) => (
        <Stack
          key={getFileKey(info.originalFile, index)}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap="1rem"
        >
          {renderFileLabel(info)}
          {renderDeleteButton(info.originalFile)}
        </Stack>
      ))}
    </FileUploadedContainer>
  );
};
